/**
 * Onboarding Provider
 * Manages onboarding flow state and progress tracking
 */

import { useState, useEffect, ReactNode } from 'react'
import {
  OnboardingContext,
  OnboardingContextType,
  OnboardingStep,
} from './OnboardingContext.context'

const ONBOARDING_STORAGE_KEY = 'axioma_onboarding_complete'

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [isOnboardingComplete, setIsOnboardingComplete] = useState(() => {
    return localStorage.getItem(ONBOARDING_STORAGE_KEY) === 'true'
  })
  const [isOnboardingActive, setIsOnboardingActive] = useState(false)
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('workspace')
  const [workspaceId, setWorkspaceId] = useState<string | null>(null)
  const [projectId, setProjectId] = useState<string | null>(null)
  const [workspaceName, setWorkspaceName] = useState<string | null>(null)
  const [projectName, setProjectName] = useState<string | null>(null)
  const [taskName, setTaskName] = useState<string | null>(null)

  // Save completion status to localStorage
  useEffect(() => {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, isOnboardingComplete.toString())
  }, [isOnboardingComplete])

  const startOnboarding = () => {
    setIsOnboardingComplete(false)
    setIsOnboardingActive(true)
    setCurrentStep('workspace')
    setWorkspaceId(null)
    setProjectId(null)
    setWorkspaceName(null)
    setProjectName(null)
    setTaskName(null)
  }

  const completeOnboarding = () => {
    setIsOnboardingComplete(true)
    setIsOnboardingActive(false)
    setCurrentStep('complete')
  }

  const skipOnboarding = () => {
    setIsOnboardingComplete(true)
    setIsOnboardingActive(false)
  }

  const nextStep = () => {
    if (currentStep === 'workspace') {
      setCurrentStep('project')
    } else if (currentStep === 'project') {
      setCurrentStep('task')
    } else if (currentStep === 'task') {
      completeOnboarding()
    }
  }

  const value: OnboardingContextType = {
    isOnboardingComplete,
    currentStep,
    workspaceId,
    projectId,
    workspaceName,
    projectName,
    taskName,
    isOnboardingActive,
    startOnboarding,
    completeOnboarding,
    skipOnboarding,
    setCurrentStep,
    setWorkspaceId,
    setProjectId,
    setWorkspaceName,
    setProjectName,
    setTaskName,
    nextStep,
  }

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>
}
